import React, { useEffect, useState, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { Card, Row, Col, Statistic, Divider, Spin, DatePicker, Select, Empty, Tag, Typography } from 'antd'
import { ArrowUpOutlined, ArrowDownOutlined, DollarOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../stores/root-store-context'
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker
const { Option } = Select
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#a4de6c', '#d0ed57', '#ffc658', '#8dd1e1']

function FinanceOverviewComponent() {
  const { t } = useTranslation()
  const { financeStore } = useStore()
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([dayjs().startOf('month'), dayjs().endOf('month')])
  const [selectedCurrency, setSelectedCurrency] = useState<number | null>(null)
  const [rates, setRates] = useState<Record<string, number>>({})
  const main = financeStore.mainCurrency

  useEffect(() => {
    if (!main?.shortName) return
    fetch(`https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${main.shortName.toLowerCase()}.json`)
      .then(r => r.json())
      .then(j => {
        if (j && j[main.shortName.toLowerCase()]) {
          setRates(j[main.shortName.toLowerCase()])
        }
      })
      .catch(err => {
        console.error('Failed to fetch currency rates:', err)
        setRates({})
      })
  }, [main])

  useEffect(() => {
    const load = async () => {
      await financeStore.getCurrencies()
      await financeStore.getOperationTypes()
      await financeStore.getOperationCategories()
      await loadOverviewData(dateRange, null)
    }
    load()
  }, [])

  const loadOverviewData = async (
    currentDateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null],
    currentCurrency: number | null
  ) => {
    if (!currentDateRange[0] || !currentDateRange[1]) return
    financeStore.overviewDateRange = currentDateRange
    financeStore.overviewSelectedCurrency = currentCurrency
    await financeStore.getOverviewTransactions(currentDateRange[0].format('YYYY-MM-DD'), currentDateRange[1].format('YYYY-MM-DD'), currentCurrency)
    await financeStore.getOverviewSummary(currentDateRange[0].format('YYYY-MM-DD'), currentDateRange[1].format('YYYY-MM-DD'), currentCurrency)
  }

  const toMain = (amount: number, short: string) => {
    if (!main || !short || !rates) return amount
    if (!main.shortName) return amount
    if (short.toLowerCase() === main.shortName.toLowerCase()) return amount
    const r = rates[short.toLowerCase()]
    if (!r) return amount
    return amount / r
  }

  const filteredTransactions = financeStore.overviewTransactions || []

  const dailyData = useMemo(() => {
    const map = new Map<string, { inc: number; exp: number }>()
    if (dateRange[0] && dateRange[1]) {
      let cur = dateRange[0].clone()
      const end = dateRange[1].clone()
      while (cur.isBefore(end) || cur.isSame(end, 'day')) {
        map.set(cur.format('YYYY-MM-DD'), { inc: 0, exp: 0 })
        cur = cur.add(1, 'day')
      }
    }
    filteredTransactions.forEach(tr => {
      const key = dayjs(tr.date).format('YYYY-MM-DD')
      if (!map.has(key)) map.set(key, { inc: 0, exp: 0 })
      const currency = financeStore.currencies.find(c => c.id === tr.currencyId)
      const short = currency?.shortName || ''
      const val = short ? toMain(tr.amount || 0, short) : (tr.amount || 0)
      const op = financeStore.operationTypes.find(o => o.id === tr.operationTypeId)
      const e = map.get(key)!
      if (op?.operationCategoryId === 1) e.inc += val
      else e.exp += val
    })
    return Array.from(map.entries())
      .map(([k, v]) => ({
        date: k,
        formattedDate: dayjs(k).format('DD MMM'),
        income: v.inc,
        expense: v.exp,
        balance: v.inc - v.exp
      }))
      .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
  }, [filteredTransactions, rates, financeStore.operationTypes, financeStore.currencies, dateRange])

  const catData = useMemo(() => {
    const map = new Map<number, number>()
    filteredTransactions.forEach(tr => {
      const currency = financeStore.currencies.find(c => c.id === tr.currencyId)
      const short = currency?.shortName || ''
      const val = short ? toMain(tr.amount || 0, short) : (tr.amount || 0)
      map.set(tr.operationTypeId, (map.get(tr.operationTypeId) ?? 0) + val)
    })
    return Array.from(map.entries()).map(([id, v]) => {
      const op = financeStore.operationTypes.find(o => o.id === id)
      return { name: op?.name ?? 'Unknown', value: v, catId: op?.operationCategoryId ?? 0 }
    })
  }, [filteredTransactions, rates, financeStore.operationTypes, financeStore.currencies])

  const incomeCats = catData.filter(c => c.catId === 1)
  const expenseCats = catData.filter(c => c.catId === 2)

  const handleDate = (dr: any) => {
    if (dr && dr.length === 2) {
      setDateRange(dr)
      loadOverviewData(dr, selectedCurrency)
    }
  }

  const handleCurrencyChange = (val: number | null) => {
    setSelectedCurrency(val)
    loadOverviewData(dateRange, val)
  }

  if (financeStore.isOverviewLoading)
    return (
      <div style={{ height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    )

  if (!financeStore.currencies || financeStore.currencies.length === 0)
    return (
      <Card style={{ textAlign: 'center' }}>
        <Empty description={t('PleaseAddCurrencyFirst')} />
      </Card>
    )

  if (!main) 
    return (
      <Card style={{ textAlign: 'center' }}>
        <Empty description={t('PleaseSetMainCurrency')} />
      </Card>
    )

  return (
    <div className="finance-overview">
      <div style={{ marginBottom: 20 }}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <RangePicker value={dateRange as any} onChange={handleDate} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} md={12}>
            <Select
              value={selectedCurrency}
              onChange={handleCurrencyChange}
              style={{ width: '100%' }}
              allowClear
              placeholder={t('AllCurrencies')}
            >
              {financeStore.currencies.map(c => (
                <Option key={c.id} value={c.id}>
                  {c.shortName} ({c.symbol})
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card bordered={false}>
            <Statistic
              title={t('TotalIncome')}
              value={financeStore.summaryTotals?.income || 0}
              precision={2}
              valueStyle={{ color: '#3f8600' }}
              prefix={<ArrowUpOutlined />}
              suffix={main?.symbol || ''}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false}>
            <Statistic
              title={t('TotalExpense')}
              value={financeStore.summaryTotals?.expense || 0}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
              prefix={<ArrowDownOutlined />}
              suffix={main?.symbol || ''}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false}>
            <Statistic
              title={t('Balance')}
              value={financeStore.summaryTotals?.balance || 0}
              precision={2}
              valueStyle={{ color: (financeStore.summaryTotals?.balance || 0) >= 0 ? '#3f8600' : '#cf1322' }}
              prefix={<DollarOutlined />}
              suffix={main?.symbol || ''}
            />
          </Card>
        </Col>
      </Row>

      <Divider>{t('IncomeVsExpense')}</Divider>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <Card bordered={false} title={t('MonthlyFinanceTrends')}>
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={dailyData} margin={{ top: 10, right: 30, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formattedDate" angle={-45} textAnchor="end" height={70} interval="preserveStartEnd" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip
                  formatter={(v: any, name: string) => {
                    const value = `${Number(v || 0).toFixed(2)} ${main?.symbol || ''}`
                    const label = name === 'income' ? t('Income') : name === 'expense' ? t('Expense') : name === 'balance' ? t('Balance') : name
                    return [value, label]
                  }}
                  labelFormatter={label => {
                    const item = dailyData.find(d => d.formattedDate === label)
                    return item ? dayjs(item.date).format('DD MMMM YYYY') : label
                  }}
                />
                <Legend />
                <Area type="monotone" dataKey="income" stroke="#4CAF50" fill="#4CAF50" fillOpacity={0.6} name={t('Income')} activeDot={{ r: 6 }} />
                <Area type="monotone" dataKey="expense" stroke="#F44336" fill="#F44336" fillOpacity={0.6} name={t('Expense')} activeDot={{ r: 6 }} />
                <Area type="monotone" dataKey="balance" stroke="#2196F3" fill="#2196F3" fillOpacity={0.3} name={t('Balance')} activeDot={{ r: 6 }} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Divider>{t('CategoryDistribution')}</Divider>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card
            bordered={false}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{t('ExpenseAnalysis')}</span>
                <Tag color="#F44336">{`${expenseCats.reduce((s, c) => s + c.value, 0).toFixed(2)} ${main?.symbol || ''}`}</Tag>
              </div>
            }
          >
            <ResponsiveContainer width="100%" height={350}>
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ height: '60%', marginBottom: 15 }}>
                  {expenseCats.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={expenseCats} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} cornerRadius={5} dataKey="value">
                          {expenseCats.map((e, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="white" strokeWidth={2} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v: number) => [`${v.toFixed(2)} ${main?.symbol || ''} (${((v / expenseCats.reduce((s, c) => s + c.value, 0)) * 100).toFixed(1)}%)`, '']}
                          labelFormatter={n => n}
                        />
                        <Legend
                          layout="vertical"
                          verticalAlign="middle"
                          align="right"
                          wrapperStyle={{ paddingLeft: '10px', fontSize: '12px' }}
                          formatter={(v, e: any) => {
                            const total = expenseCats.reduce((s, c) => s + c.value, 0)
                            const p = total > 0 ? ((e.payload.value / total) * 100).toFixed(1) : '0'
                            return `${v} (${p}%)`
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Empty description={t('NoExpenseData')} />
                  )}
                </div>
                <div style={{ height: '40%' }}>
                  <Typography.Text style={{ display: 'block', marginBottom: 8, textAlign: 'center' }}>{t('TopExpensesByCategory')}</Typography.Text>
                  {expenseCats.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[...expenseCats].sort((a, b) => b.value - a.value).slice(0, 5)} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" hide />
                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v: number) => `${v.toFixed(2)} ${main?.symbol || ''}`} />
                        <Bar dataKey="value" fill="#F44336" background={{ fill: '#eee' }} label={{ position: 'right', formatter: (v: any) => `${Number(v).toFixed(0)} ${main?.symbol || ''}`, fontSize: 12 }} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Empty description={t('NoExpenseData')} />
                  )}
                </div>
              </div>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            bordered={false}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{t('IncomeAnalysis')}</span>
                <Tag color="#4CAF50">{`${incomeCats.reduce((s, c) => s + c.value, 0).toFixed(2)} ${main?.symbol || ''}`}</Tag>
              </div>
            }
          >
            <ResponsiveContainer width="100%" height={350}>
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ height: '60%', marginBottom: 15 }}>
                  {incomeCats.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={incomeCats} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} cornerRadius={5} dataKey="value">
                          {incomeCats.map((e, i) => (
                            <Cell key={i} fill={['#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107'][i % 5]} stroke="white" strokeWidth={2} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v: number) => [`${v.toFixed(2)} ${main?.symbol || ''} (${((v / incomeCats.reduce((s, c) => s + c.value, 0)) * 100).toFixed(1)}%)`, '']}
                          labelFormatter={n => n}
                        />
                        <Legend
                          layout="vertical"
                          verticalAlign="middle"
                          align="right"
                          wrapperStyle={{ paddingLeft: '10px', fontSize: '12px' }}
                          formatter={(v, e: any) => {
                            const total = incomeCats.reduce((s, c) => s + c.value, 0)
                            const p = total > 0 ? ((e.payload.value / total) * 100).toFixed(1) : '0'
                            return `${v} (${p}%)`
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Empty description={t('NoIncomeData')} />
                  )}
                </div>
                <div style={{ height: '40%' }}>
                  <Typography.Text style={{ display: 'block', marginBottom: 8, textAlign: 'center' }}>{t('TopIncomeSources')}</Typography.Text>
                  {incomeCats.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[...incomeCats].sort((a, b) => b.value - a.value).slice(0, 5)} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" hide />
                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v: number) => `${v.toFixed(2)} ${main?.symbol || ''}`} />
                        <Bar dataKey="value" fill="#4CAF50" background={{ fill: '#eee' }} label={{ position: 'right', formatter: (v: any) => `${Number(v).toFixed(0)} ${main?.symbol || ''}`, fontSize: 12 }} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Empty description={t('NoIncomeData')} />
                  )}
                </div>
              </div>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

const FinanceOverview = observer(FinanceOverviewComponent)
export default FinanceOverview